import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2, Building2, Truck, ArrowLeft, MapPin } from 'lucide-react';
import { apiService } from '../../services/apiService';

export default function CustomerShipToSearchModal({ initialStep, selectedCustomer, correlationId, user, onSelect, onClose }) {
  const [step, setStep] = useState(initialStep); // 'customer' | 'shipto'
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState(null);
  const [hasSearchedCustomers, setHasSearchedCustomers] = useState(false);

  const [chosenCustomer, setChosenCustomer] = useState(
    initialStep === 'shipto' && selectedCustomer?.number
      ? { customer_number: selectedCustomer.number, customer_name: selectedCustomer.name }
      : null
  );

  const [shipToResults, setShipToResults] = useState([]);
  const [shipToLoading, setShipToLoading] = useState(false);
  const [shipToError, setShipToError] = useState(null);
  const [shipToFilter, setShipToFilter] = useState('');

  const customerInputRef = useRef(null);
  const shipToFilterRef = useRef(null);

  // Auto-focus customer input on mount (step 1)
  useEffect(() => {
    if (step === 'customer') {
      customerInputRef.current?.focus();
    }
  }, [step]);

  // Auto-focus ship-to filter when entering step 2
  useEffect(() => {
    if (step === 'shipto') {
      shipToFilterRef.current?.focus();
    }
  }, [step]);

  // Debounced customer search
  useEffect(() => {
    if (step !== 'customer') return;
    if (!customerQuery.trim()) {
      setCustomerResults([]);
      setHasSearchedCustomers(false);
      setCustomerError(null);
      return;
    }

    const timer = setTimeout(async () => {
      setCustomerLoading(true);
      setCustomerError(null);
      try {
        const data = await apiService.searchCustomers(
          customerQuery.trim(),
          correlationId,
          user?.userId,
          user?.userEmail
        );
        setCustomerResults(data.results || []);
        setHasSearchedCustomers(true);
      } catch (err) {
        console.error('Customer search failed:', err);
        setCustomerError(err.message || 'Search failed');
        setCustomerResults([]);
      } finally {
        setCustomerLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [customerQuery, step, correlationId, user]);

  // Fetch ship-tos when entering step 2 with a chosen customer
  const fetchShipTos = useCallback(async (customerNumber) => {
    setShipToLoading(true);
    setShipToError(null);
    setShipToResults([]);
    setShipToFilter('');
    try {
      const data = await apiService.searchShipTos(
        customerNumber,
        correlationId,
        user?.userId,
        user?.userEmail
      );
      setShipToResults(data.results || []);
      // Update chosen customer name from response if available
      if (data.customer_name) {
        setChosenCustomer(prev => prev ? { ...prev, customer_name: data.customer_name } : prev);
      }
    } catch (err) {
      console.error('Ship-to search failed:', err);
      setShipToError(err.message || 'Failed to load ship-to locations');
    } finally {
      setShipToLoading(false);
    }
  }, [correlationId, user]);

  // Auto-fetch ship-tos when step changes to 'shipto'
  useEffect(() => {
    if (step === 'shipto' && chosenCustomer?.customer_number) {
      fetchShipTos(chosenCustomer.customer_number);
    }
  }, [step, chosenCustomer?.customer_number, fetchShipTos]);

  const handleSelectCustomer = (customer) => {
    setChosenCustomer(customer);
    setStep('shipto');
  };

  const handleSelectShipTo = (shipTo) => {
    onSelect(chosenCustomer, shipTo);
  };

  const handleBackToCustomers = () => {
    setStep('customer');
    setShipToResults([]);
    setShipToFilter('');
    setShipToError(null);
  };

  // Client-side filter for ship-to results
  const filteredShipTos = shipToFilter.trim()
    ? shipToResults.filter(s => {
        const q = shipToFilter.toLowerCase();
        return (
          s.shipto_number.toLowerCase().includes(q) ||
          s.shipto_name.toLowerCase().includes(q) ||
          s.shipto_address.toLowerCase().includes(q) ||
          s.shipto_city.toLowerCase().includes(q) ||
          s.shipto_state.toLowerCase().includes(q) ||
          s.shipto_zip.toLowerCase().includes(q)
        );
      })
    : shipToResults;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center animate-fadeIn"
    >
      <div
        className="bg-slate-800 border border-slate-600 rounded-xl shadow-2xl w-full max-w-lg mx-4 animate-scaleIn flex flex-col max-h-[80vh]"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
              {step === 'customer'
                ? <Building2 className="w-4 h-4 text-amber-400" />
                : <Truck className="w-4 h-4 text-amber-400" />
              }
            </div>
            <div>
              <h3 className="text-white font-semibold">
                {step === 'customer' ? 'Find Customer' : 'Select Ship-To'}
              </h3>
              <p className="text-xs text-slate-400">
                {step === 'customer'
                  ? 'Search by customer name or number'
                  : <>Customer: {chosenCustomer?.customer_name} (#{chosenCustomer?.customer_number})</>
                }
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step 1: Customer Search */}
        {step === 'customer' && (
          <>
            <div className="px-5 py-3 border-b border-slate-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  ref={customerInputRef}
                  type="text"
                  value={customerQuery}
                  onChange={e => setCustomerQuery(e.target.value)}
                  placeholder="Search by customer name or number..."
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-10 pr-10 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 transition-colors"
                />
                {customerLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-400 animate-spin" />
                )}
                {!customerLoading && customerQuery && (
                  <button
                    onClick={() => setCustomerQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-auto min-h-0">
              {customerError && (
                <div className="px-5 py-4 text-sm text-red-400">{customerError}</div>
              )}

              {!customerError && hasSearchedCustomers && customerResults.length === 0 && !customerLoading && (
                <div className="px-5 py-8 text-center">
                  <Building2 className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No customers found for "{customerQuery}"</p>
                  <p className="text-xs text-slate-500 mt-1">Try a different name or customer number</p>
                </div>
              )}

              {!customerError && !hasSearchedCustomers && !customerLoading && (
                <div className="px-5 py-8 text-center">
                  <Search className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Search the A+ customer database</p>
                  <p className="text-xs text-slate-500 mt-1">Type a customer name or number to begin</p>
                </div>
              )}

              {customerResults.length > 0 && (
                <div className="divide-y divide-slate-700/50">
                  {customerResults.map((result, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectCustomer(result)}
                      className="w-full text-left px-5 py-3 hover:bg-slate-700/50 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-white text-sm font-medium">{result.customer_name}</div>
                          <div className="text-xs text-slate-400 font-mono">#{result.customer_number}</div>
                        </div>
                        <span className="text-xs text-slate-500">Select &rarr;</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Step 2: Ship-To Selection */}
        {step === 'shipto' && (
          <>
            <div className="px-5 py-3 border-b border-slate-700">
              <div className="flex items-center gap-2">
                {/* Back button only if user came from customer search (not direct ship-to entry) */}
                {initialStep === 'customer' || !selectedCustomer?.number ? (
                  <button
                    onClick={handleBackToCustomers}
                    className="text-slate-400 hover:text-amber-400 transition-colors p-1"
                    title="Back to customer search"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                ) : null}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    ref={shipToFilterRef}
                    type="text"
                    value={shipToFilter}
                    onChange={e => setShipToFilter(e.target.value)}
                    placeholder="Filter ship-to locations..."
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-10 pr-10 py-2.5 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30 transition-colors"
                  />
                  {shipToFilter && (
                    <button
                      onClick={() => setShipToFilter('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-auto min-h-0">
              {shipToLoading && (
                <div className="px-5 py-8 text-center">
                  <Loader2 className="w-8 h-8 text-amber-400 mx-auto mb-2 animate-spin" />
                  <p className="text-sm text-slate-400">Loading ship-to locations...</p>
                </div>
              )}

              {shipToError && (
                <div className="px-5 py-4 text-sm text-red-400">{shipToError}</div>
              )}

              {!shipToLoading && !shipToError && filteredShipTos.length === 0 && shipToResults.length > 0 && (
                <div className="px-5 py-8 text-center">
                  <MapPin className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No ship-to locations match "{shipToFilter}"</p>
                </div>
              )}

              {!shipToLoading && !shipToError && shipToResults.length === 0 && (
                <div className="px-5 py-8 text-center">
                  <MapPin className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No ship-to locations found for this customer</p>
                </div>
              )}

              {!shipToLoading && filteredShipTos.length > 0 && (
                <div className="divide-y divide-slate-700/50">
                  {!shipToFilter && (
                    <div className="px-5 py-2 text-xs text-slate-500 bg-slate-800 sticky top-0">
                      {shipToResults.length} location{shipToResults.length !== 1 ? 's' : ''}
                    </div>
                  )}
                  {shipToFilter && (
                    <div className="px-5 py-2 text-xs text-slate-500 bg-slate-800 sticky top-0">
                      {filteredShipTos.length} of {shipToResults.length} location{shipToResults.length !== 1 ? 's' : ''}
                    </div>
                  )}
                  {filteredShipTos.map((shipTo, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSelectShipTo(shipTo)}
                      className="w-full text-left px-5 py-3 hover:bg-slate-700/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-white text-sm">#{shipTo.shipto_number}</span>
                            <span className="text-white text-sm">{shipTo.shipto_name}</span>
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">{shipTo.shipto_address}</div>
                          {shipTo.shipto_address2 && (
                            <div className="text-xs text-slate-400">{shipTo.shipto_address2}</div>
                          )}
                          <div className="text-xs text-slate-400">
                            {shipTo.shipto_city}, {shipTo.shipto_state} {shipTo.shipto_zip}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-700 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {step === 'customer' ? 'Step 1 of 2' : 'Step 2 of 2'}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
